/**
 * McpPanel.jsx
 * The switch that lets Claude connect, and the token it connects with.
 *
 * Written to make the trade visible rather than buried. This is the one part
 * of the app that sits outside Cloudflare Access — it has to, because an MCP
 * client cannot complete a browser login — so the token is the only thing
 * guarding it. The panel says that where you can see it, next to the switch,
 * not in a document nobody opens.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning } from './Icons.jsx';
import { formatLondon } from '../utils/time.js';

export default function McpPanel() {
  const [data, setData] = useState(null);
  const [fresh, setFresh] = useState(null); // the token, shown once
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.settings.mcp());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function toggle(on) {
    setBusy(true);
    setError(null);
    try {
      await api.settings.update({ mcp_enabled: on });
      // Switching it on with no token is a dead end, so one is made here.
      if (on && !data?.token?.exists) {
        const res = await api.settings.newMcpToken();
        setFresh(res.token);
      }
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (
      !confirm(
        'Generate a new token?\n\nThe current one stops working immediately, and anything using it will need the new one.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.settings.newMcpToken();
      setFresh(res.token);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!confirm('Revoke the token? Claude will not be able to connect until a new one is made.')) {
      return;
    }
    setBusy(true);
    try {
      await api.settings.revokeMcpToken();
      setFresh(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!data) return null;

  const on = data.enabled;

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Let Claude connect</h2>
          <p>
            Gives Claude read access to this app and the ability to prepare drafts, over MCP.
          </p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <div className="row">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="row__label">{on ? 'Connected apps can reach Kreeboom' : 'Switched off'}</div>
          <div className="row__sub">
            {on
              ? 'Anything holding the token below can read your brands, posts and figures.'
              : 'Nothing can reach the MCP endpoint. This is the safe default.'}
          </div>
        </div>
        <div className="row__end">
          <button
            className={`switch${on ? ' switch--on' : ''}`}
            role="switch"
            aria-checked={on}
            aria-label="Let Claude connect"
            disabled={busy}
            onClick={() => toggle(!on)}
          >
            <span className="switch__dot" />
          </button>
        </div>
      </div>

      {/* The one time the token is ever visible. */}
      {fresh && (
        <div className="notice notice--info" style={{ display: 'block' }}>
          <p style={{ marginBottom: 8 }}>
            <strong>Copy this now.</strong> It is stored only as a hash and cannot be shown again.
          </p>
          <code className="token">{fresh}</code>
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button
              className="btn btn--sm"
              onClick={() => {
                navigator.clipboard?.writeText(fresh);
                setCopied(true);
              }}
            >
              {copied ? <Check size={15} /> : null} {copied ? 'Copied' : 'Copy token'}
            </button>
            <button className="btn btn--sm" onClick={() => setFresh(null)}>
              I have saved it
            </button>
          </div>
        </div>
      )}

      {on && (
        <>
          <div className="field" style={{ marginTop: 14 }}>
            <label>Server URL</label>
            <code className="token">{data.url}</code>
            <p className="hint">
              Paste this into Claude&rsquo;s <strong>Settings → Connectors → Add</strong>. Claude
              registers itself and opens a sign-in page; because that page is behind Cloudflare
              Access, signing in is how it knows the request is really from you. You will then see
              exactly what it is being granted before you press Connect.
            </p>
          </div>

          <div className="row">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="row__label">
                Manual token {data.token.exists ? '· active' : '· none'}
              </div>
              <div className="row__sub">
                {data.token.exists
                  ? `Made ${formatLondon(data.token.createdAt)}. ${
                      data.token.lastUsedAt
                        ? `Last used ${formatLondon(data.token.lastUsedAt)}.`
                        : 'Not used yet.'
                    }`
                  : 'Only needed for a client that cannot do OAuth.'}
              </div>
              <p className="hint">
                Claude does not need this — it signs in above. This is for a script or a client
                that only takes a bearer token.
              </p>
            </div>
            <div className="row__end">
              <button className="btn btn--sm" onClick={regenerate} disabled={busy}>
                {data.token.exists ? 'New token' : 'Generate token'}
              </button>
              {data.token.exists && (
                <button className="btn btn--sm btn--danger" onClick={revoke} disabled={busy}>
                  Revoke
                </button>
              )}
            </div>
          </div>

          <div className="notice notice--info">
            <Check />
            <span>{data.cannot}</span>
          </div>

          <details className="tool-list">
            <summary>What Claude can do ({data.tools.length} tools)</summary>
            <ul>
              {data.tools.map((t) => (
                <li key={t.name}>
                  <code>{t.name}</code> — {t.description}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
